import React from 'react';
import { CaretDown, CaretUp } from '@phosphor-icons/react';
import { StatusPill } from '../../../../components/immobilien/ui';
import { kurzDatum, standText, type SortFeld, type SortRichtung, type ZaehlerZeile } from '../../../../lib/zaehler/liste';
import { TypChip, TypIcon } from '../teile';

const SPALTEN = 'minmax(170px,1.2fr) 110px minmax(160px,1.6fr) minmax(120px,1fr) 90px';
const KOEPFE: readonly { feld: SortFeld; label: string }[] = [
  { feld: 'nummer', label: 'Zählernr.' }, { feld: 'typ', label: 'Typ' }, { feld: 'objekt', label: 'Objekt / Einheit' }, { feld: 'stand', label: 'Letzter Stand' },
];

/** Tabelle (Handoff Zähler §5.1): vier sortierbare Spalten, Status. */
export function ZaehlerTabelle({ zeilen, sortierung, onSortieren, onOeffnen }: {
  zeilen: readonly ZaehlerZeile[];
  sortierung: { feld: SortFeld; richtung: SortRichtung };
  onSortieren: (feld: SortFeld) => void;
  onOeffnen: (id: string) => void;
}) {
  const tastatur = (id: string) => (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOeffnen(id); }
  };
  return (
    <section className="immo-glas overflow-x-auto" style={{ borderRadius: 26, padding: 6 }} aria-label="Zähler als Tabelle">
      <div role="table" className="flex flex-col" style={{ minWidth: 720 }}>
        <div role="row" className="grid" style={{ gridTemplateColumns: SPALTEN, gap: 14, padding: '12px 16px 10px' }}>
          {KOEPFE.map(k => {
            const aktiv = sortierung.feld === k.feld;
            const Pfeil = sortierung.richtung === 'asc' ? CaretUp : CaretDown;
            return (
              <span key={k.feld} role="columnheader" className="flex items-center" aria-sort={aktiv ? (sortierung.richtung === 'asc' ? 'ascending' : 'descending') : 'none'}>
                <button type="button" onClick={() => onSortieren(k.feld)} className="inline-flex items-center cursor-pointer"
                  style={{ gap: 4, padding: 0, border: 'none', background: 'transparent', fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: aktiv ? 'var(--ink)' : 'var(--ink3)', fontFamily: 'inherit' }}>
                  {k.label}{aktiv && <Pfeil size={11} weight="bold" />}
                </button>
              </span>
            );
          })}
          <span role="columnheader" className="flex items-center" style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--ink3)' }}>Status</span>
        </div>
        {zeilen.map(z => (
          <div key={z.id} role="row" tabIndex={0} aria-label={`Zähler ${z.zaehler_nummer} öffnen`} onClick={() => onOeffnen(z.id)} onKeyDown={tastatur(z.id)}
            className="immo-hover-flaeche grid items-center cursor-pointer"
            style={{ gridTemplateColumns: SPALTEN, gap: 14, padding: '11px 16px', borderRadius: 18, fontSize: 14 }}>
            <span role="cell" className="flex items-center min-w-0" style={{ gap: 10 }}>
              <TypIcon typ={z.typ} groesse={36} />
              <b className="immo-zahl truncate" style={{ fontWeight: 800 }}>{z.zaehler_nummer}</b>
            </span>
            <span role="cell" className="flex min-w-0"><TypChip typ={z.typ} groesse={24} /></span>
            <span role="cell" className="flex flex-col min-w-0">
              <span className="truncate" style={{ fontWeight: 700 }}>{z.objekt?.bezeichnung || '—'}</span>
              <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>
                {z.objekt?.objektnummer && <b style={{ fontWeight: 800, color: 'var(--acc)' }}>{z.objekt.objektnummer}</b>}
                {z.objekt?.objektnummer && z.einheit_bezeichnung && ' · '}
                {z.einheit_bezeichnung}
              </span>
            </span>
            <span role="cell" className="flex flex-col min-w-0">
              {z.letzteAblesung ? (
                <>
                  <b className="immo-zahl truncate" style={{ fontWeight: 800 }}>{standText(z.letzteAblesung.wert, z.einheit)}</b>
                  <span className="immo-zahl" style={{ fontSize: 12, color: 'var(--ink3)' }}>{kurzDatum(z.letzteAblesung.datum)}</span>
                </>
              ) : <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Keine Ablesung</span>}
            </span>
            <span role="cell" className="flex min-w-0"><StatusPill status={z.aktiv ? 'aktiv' : 'inaktiv'} groesse="s" /></span>
          </div>
        ))}
      </div>
    </section>
  );
}
